import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  Card,
  Col,
  ConfigProvider,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
  notification,
  Badge,
  Upload,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  AppstoreOutlined,
  BranchesOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CloudOutlined,
  CoffeeOutlined,
  CompassOutlined,
  CrownOutlined,
  DeleteOutlined,
  EditOutlined,
  ExperimentOutlined,
  EyeOutlined,
  FilterOutlined,
  FireOutlined,
  GiftOutlined,
  HeartOutlined,
  HolderOutlined,
  HomeOutlined,
  MobileOutlined,
  PictureOutlined,
  PlusOutlined,
  ReloadOutlined,
  RocketOutlined,
  SaveOutlined,
  SearchOutlined,
  SettingOutlined,
  ShopOutlined,
  ShoppingOutlined,
  ShoppingCartOutlined,
  SmileOutlined,
  StarFilled,
  StarOutlined,
  SunOutlined,
  SyncOutlined,
  TagOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  TrophyOutlined,
  UploadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../config/firebase';

import AdminLayout from '../components/AdminLayout';
import { miniAppService } from '../services/miniAppService';
import { messageService } from '../services/messageService';
import {
  MiniAppProductsConfig,
  MiniAppCategory,
  CukcukCategoryGroup,
  MiniAppForYouConfig,
  MiniAppSearchConfig,
  MiniAppCartConfig,
  MiniAppProfileConfig,
  MiniAppPageKey,
} from '../types/miniAppManagement';
import { Dish } from '../types/dish';
import viVN from 'antd/locale/vi_VN';
import '../styles/AdminPages.css';
import '../styles/MiniAppManagement.css';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

// Thư viện Icon phong phú cho danh mục thực đơn & đồ uống
export const AVAILABLE_ICONS = [
  { key: 'FireOutlined', label: 'Ngọn lửa (Specialty)', component: FireOutlined },
  { key: 'ExperimentOutlined', label: 'Bình chiết xuất (Cold Brew)', component: ExperimentOutlined },
  { key: 'CoffeeOutlined', label: 'Cốc cà phê (Arabica)', component: CoffeeOutlined },
  { key: 'ThunderboltOutlined', label: 'Tia sét (Robusta / Đậm)', component: ThunderboltOutlined },
  { key: 'BranchesOutlined', label: 'Cành thảo mộc (Chai tea)', component: BranchesOutlined },
  { key: 'CloudOutlined', label: 'Đám mây (Non Caffein)', component: CloudOutlined },
  { key: 'ShopOutlined', label: 'Hạt cà phê / Quán', component: ShopOutlined },
  { key: 'ShoppingOutlined', label: 'Đồ đóng chai / Mua sắm', component: ShoppingOutlined },
  { key: 'ToolOutlined', label: 'Dụng cụ pha chế / Máy', component: ToolOutlined },
  { key: 'FilterOutlined', label: 'Phễu lọc Pour Over / V60', component: FilterOutlined },
  { key: 'CrownOutlined', label: 'Vương miện (Signature)', component: CrownOutlined },
  { key: 'StarOutlined', label: 'Ngôi sao (Nổi bật)', component: StarOutlined },
  { key: 'TrophyOutlined', label: 'Cúp vàng (Best seller)', component: TrophyOutlined },
  { key: 'HeartOutlined', label: 'Trái tim (Yêu thích)', component: HeartOutlined },
  { key: 'SmileOutlined', label: 'Mặt cười (Trà hoa / Thư giãn)', component: SmileOutlined },
  { key: 'SunOutlined', label: 'Mặt trời (Morning Brew)', component: SunOutlined },
  { key: 'GiftOutlined', label: 'Hộp quà (Combo / Ưu đãi)', component: GiftOutlined },
  { key: 'TagOutlined', label: 'Thẻ giá / Nhãn đặc biệt', component: TagOutlined },
  { key: 'RocketOutlined', label: 'Tên lửa (Món mới ra mắt)', component: RocketOutlined },
  { key: 'BulbOutlined', label: 'Bóng đèn (Sáng tạo)', component: BulbOutlined },
  { key: 'CompassOutlined', label: 'La bàn (Vùng trồng)', component: CompassOutlined },
  { key: 'AppstoreOutlined', label: 'Menu tổng hợp', component: AppstoreOutlined },
];

export const renderCategoryIcon = (iconName?: string, style?: React.CSSProperties) => {
  const item = AVAILABLE_ICONS.find((i) => i.key === iconName);
  const IconComp = item ? item.component : CoffeeOutlined;
  return <IconComp style={style} />;
};

// Row kéo thả cho bảng danh mục
interface DraggableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  'data-row-key': string;
}

const DraggableRow: React.FC<DraggableRowProps> = ({ children, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props['data-row-key'],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 9999, background: '#f0f9ff' } : {}),
    cursor: 'default',
  };

  return (
    <tr {...props} ref={setNodeRef} style={style} className={isDragging ? 'dragging-row' : ''}>
      {React.Children.map(children, (child) => {
        if ((child as any)?.key === 'sort') {
          return React.cloneElement(child as React.ReactElement, {
            children: (
              <div {...attributes} {...listeners} style={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}>
                <HolderOutlined style={{ color: '#9ca3af', fontSize: '16px' }} />
              </div>
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};

// Preset màu sắc & gradient cho danh mục
const PRESET_THEMES = [
  { name: 'Nâu Cà phê (Specialty)', color: '#8B4513', gradient: 'linear-gradient(135deg, #FFF8E7 0%, #FFE0B2 100%)', iconName: 'FireOutlined' },
  { name: 'Xanh Đại dương (Cold Brew)', color: '#1565C0', gradient: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)', iconName: 'ExperimentOutlined' },
  { name: 'Cam Caramel (Arabica)', color: '#C67C4E', gradient: 'linear-gradient(135deg, #FFF3E0 0%, #FFE082 100%)', iconName: 'CoffeeOutlined' },
  { name: 'Nâu Đậm (Robusta)', color: '#4E342E', gradient: 'linear-gradient(135deg, #EFEBE9 0%, #D7CCC8 100%)', iconName: 'ThunderboltOutlined' },
  { name: 'Hồng Gia vị (Chai)', color: '#AD1457', gradient: 'linear-gradient(135deg, #FCE4EC 0%, #F8BBD0 100%)', iconName: 'BranchesOutlined' },
  { name: 'Xanh Lá (Non Caffein)', color: '#2E7D32', gradient: 'linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)', iconName: 'CloudOutlined' },
  { name: 'Tím Quý phái (Máy pha)', color: '#6A1B9A', gradient: 'linear-gradient(135deg, #F3E5F5 0%, #E1BEE7 100%)', iconName: 'ToolOutlined' },
  { name: 'Xanh Ngọc (Đóng chai)', color: '#00838F', gradient: 'linear-gradient(135deg, #E0F7FA 0%, #80DEEA 100%)', iconName: 'ShoppingOutlined' },
];

const MiniAppManagement: React.FC = () => {
  // Main State
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('products');

  // Config State
  const [productsConfig, setProductsConfig] = useState<MiniAppProductsConfig>(
    miniAppService.generateDefaultProductsConfig()
  );
  const [forYouConfig, setForYouConfig] = useState<MiniAppForYouConfig | null>(null);
  const [searchConfig, setSearchConfig] = useState<MiniAppSearchConfig | null>(null);
  const [cartConfig, setCartConfig] = useState<MiniAppCartConfig | null>(null);
  const [profileConfig, setProfileConfig] = useState<MiniAppProfileConfig | null>(null);

  // Cukcuk Data
  const [cukcukGroups, setCukcukGroups] = useState<CukcukCategoryGroup[]>([]);

  // Modals state
  const [editCategoryModalVisible, setEditCategoryModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<MiniAppCategory | null>(null);
  const [addCategoryModalVisible, setAddCategoryModalVisible] = useState(false);
  const [manageDishesDrawerVisible, setManageDishesDrawerVisible] = useState(false);
  const [selectedCategoryForDishes, setSelectedCategoryForDishes] = useState<MiniAppCategory | null>(null);
  const [dishSearchTerm, setDishSearchTerm] = useState('');

  // Danh sách sản phẩm đa nguồn
  const [coffeeList, setCoffeeList] = useState<any[]>([]);
  const [bottledDrinkList, setBottledDrinkList] = useState<any[]>([]);
  const [equipmentList, setEquipmentList] = useState<any[]>([]);
  const [newsMessageList, setNewsMessageList] = useState<any[]>([]);

  // Simulator preview state
  const [simulatorActiveTab, setSimulatorActiveTab] = useState<string>('products');
  const [simulatorSelectedCat, setSimulatorSelectedCat] = useState<string>('all');
  const [showSimulator, setShowSimulator] = useState(true);

  // Forms
  const [categoryForm] = Form.useForm();
  const [addCategoryForm] = Form.useForm();
  const [uploadingImage, setUploadingImage] = useState(false);

  // Form watchers để xem trước trực quan thời gian thực trong Modal sửa
  const editFormName = Form.useWatch('name', categoryForm);
  const editFormSubtitle = Form.useWatch('subtitle', categoryForm);
  const editFormBadge = Form.useWatch('badge', categoryForm);
  const editFormImageUrl = Form.useWatch('imageUrl', categoryForm);
  const editFormIconName = Form.useWatch('iconName', categoryForm);
  const editFormColor = Form.useWatch('colorTheme', categoryForm);
  const editFormGradient = Form.useWatch('gradient', categoryForm);

  // Form watchers để xem trước trực quan thời gian thực trong Modal thêm
  const addFormName = Form.useWatch('name', addCategoryForm);
  const addFormSubtitle = Form.useWatch('subtitle', addCategoryForm);
  const addFormBadge = Form.useWatch('badge', addCategoryForm);
  const addFormImageUrl = Form.useWatch('imageUrl', addCategoryForm);
  const addFormIconName = Form.useWatch('iconName', addCategoryForm);
  const addFormColor = Form.useWatch('colorTheme', addCategoryForm);
  const addFormGradient = Form.useWatch('gradient', addCategoryForm);

  // Sensors cho drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  // Khởi tạo dữ liệu
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      // 1. Tải song song tất cả các nguồn dữ liệu
      const [groups, coffees, bottledDrinks, equipment, allMessages, prodConfig] = await Promise.all([
        miniAppService.fetchCukcukCategoryGroups(),
        miniAppService.fetchCoffees(),
        miniAppService.fetchBottledDrinks(),
        miniAppService.fetchEquipment(),
        messageService.getAllMessages().catch(() => []),
        miniAppService.getProductsPageConfig(),
      ]);

      const newsWithBanner = (allMessages || []).filter(
        (m: any) => m.type === 'template' && m.template_data?.banner?.image_url
      );

      setCukcukGroups(groups || []);
      setCoffeeList(coffees || []);
      setBottledDrinkList(bottledDrinks || []);
      setEquipmentList(equipment || []);
      setNewsMessageList(newsWithBanner || []);

      let baseCategories = [...(prodConfig.categories || [])];

      // Tự động bổ sung khối Tin tức & Ưu đãi vào bảng nếu chưa có
      if (!baseCategories.some((c) => c.sourceType === 'news' || c.code === 'news')) {
        baseCategories.push({
          id: 'cat_news',
          code: 'news',
          name: 'Tin tức & Ưu đãi',
          subtitle: 'Bản tin, chương trình ưu đãi mới nhất',
          sourceType: 'news',
          iconName: 'TagOutlined',
          colorTheme: '#7C3AED',
          gradient: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)',
          displayOrder: baseCategories.length + 1,
          isActive: true,
          badge: 'HOT',
          displayLayout: 'horizontal_scroll',
          showInMainCategories: false,
          showAsFeaturedSection: true,
        });
      }

      // 2. Cập nhật số lượng món thực tế cho từng danh mục / section
      const updatedCategories = baseCategories.map((cat) => {
        let itemCount = 0;
        let sampleImage = cat.imageUrl;

        if (cat.sourceType === 'coffee') {
          itemCount = (coffees || []).length;
          const firstWithImg: any = (coffees || []).find((c: any) => c.images?.[0] || c.imageUrl);
          sampleImage = sampleImage || firstWithImg?.images?.[0] || firstWithImg?.imageUrl;
        } else if (cat.sourceType === 'bottledDrink') {
          itemCount = (bottledDrinks || []).length;
          const firstWithImg: any = (bottledDrinks || []).find((d: any) => d.images?.[0] || (d as any).imageUrl);
          sampleImage = sampleImage || firstWithImg?.images?.[0] || firstWithImg?.imageUrl;
        } else if (cat.sourceType === 'equipment') {
          itemCount = (equipment || []).length;
          const firstWithImg: any = (equipment || []).find((e: any) => e.images?.[0] || (e as any).imageUrl);
          sampleImage = sampleImage || firstWithImg?.images?.[0] || firstWithImg?.imageUrl;
        } else if (cat.sourceType === 'news' || cat.code === 'news') {
          itemCount = newsWithBanner.length;
          sampleImage = sampleImage || newsWithBanner[0]?.template_data?.banner?.image_url;
        } else {
          // CUKCUK
          const matchingGroup = (groups || []).find(
            (g) =>
              g.groupName.toLowerCase() === (cat.cukcukGroupName || cat.name).toLowerCase() ||
              g.groupCode.toLowerCase() === cat.code.toLowerCase()
          );
          itemCount = matchingGroup ? matchingGroup.dishCount : cat.itemCount || 0;
          sampleImage = sampleImage || matchingGroup?.sampleImageUrl;
        }

        return {
          ...cat,
          itemCount,
          imageUrl: sampleImage,
        };
      });

      setProductsConfig({
        ...prodConfig,
        categories: updatedCategories,
      });

      // 3. Lấy cấu hình các trang khác
      const [forYou, search, cart, profile] = await Promise.all([
        miniAppService.getForYouPageConfig(),
        miniAppService.getSearchPageConfig(),
        miniAppService.getCartPageConfig(),
        miniAppService.getProfilePageConfig(),
      ]);

      setForYouConfig(forYou);
      setSearchConfig(search);
      setCartConfig(cart);
      setProfileConfig(profile);
    } catch (error) {
      console.error('Error loading Mini App configs:', error);
      message.error('Không thể tải cấu hình Mini App');
    } finally {
      setLoading(false);
    }
  };

  // Đồng bộ danh mục từ CUKCUK
  const handleSyncFromCukcuk = async () => {
    setSyncing(true);
    try {
      const synced = await miniAppService.syncCategoriesWithCukcuk(productsConfig.categories);
      const groups = await miniAppService.fetchCukcukCategoryGroups();
      setCukcukGroups(groups || []);

      setProductsConfig((prev) => ({
        ...prev,
        categories: synced.map((cat) => {
          let itemCount = cat.itemCount || 0;
          let sampleImg = cat.imageUrl;

          if (cat.sourceType === 'coffee') {
            itemCount = coffeeList.length;
          } else if (cat.sourceType === 'bottledDrink') {
            itemCount = bottledDrinkList.length;
          } else if (cat.sourceType === 'equipment') {
            itemCount = equipmentList.length;
          } else if (cat.sourceType === 'news' || cat.code === 'news') {
            itemCount = newsMessageList.length;
          } else {
            const matchingGroup = (groups || []).find(
              (g) =>
                g.groupName.toLowerCase() === (cat.cukcukGroupName || cat.name).toLowerCase() ||
                g.groupCode.toLowerCase() === cat.code.toLowerCase()
            );
            if (matchingGroup) {
              itemCount = matchingGroup.dishCount;
              sampleImg = sampleImg || matchingGroup.sampleImageUrl;
            }
          }

          return {
            ...cat,
            itemCount,
            imageUrl: sampleImg,
          };
        }),
      }));

      message.success(`Đã quét và đồng bộ ${synced.length} danh mục & section thành công!`);
    } catch (error) {
      console.error('Sync error:', error);
      message.error('Lỗi khi đồng bộ từ CUKCUK');
    } finally {
      setSyncing(false);
    }
  };

  // Khôi phục mặc định
  const handleResetDefaults = () => {
    Modal.confirm({
      title: 'Khôi phục danh mục CUKCUK mặc định?',
      content:
        'Thao tác này sẽ đặt lại danh sách danh mục theo cấu hình CUKCUK chuẩn (Specialty, Cold Brew, Arabica Base, Robusta Base, Chai, Non Caffein,...). Bạn có muốn tiếp tục?',
      okText: 'Khôi phục',
      cancelText: 'Hủy',
      okType: 'danger',
      onOk: () => {
        const defaultConfig = miniAppService.generateDefaultProductsConfig();
        setProductsConfig(defaultConfig);
        message.info('Đã khôi phục danh mục CUKCUK mặc định');
      },
    });
  };

  // Lưu & Xuất bản lên Firestore
  const handleSaveAndPublish = async () => {
    setSaving(true);
    try {
      await miniAppService.saveProductsPageConfig(productsConfig);

      if (forYouConfig) await miniAppService.saveForYouPageConfig(forYouConfig);
      if (searchConfig) await miniAppService.saveSearchPageConfig(searchConfig);
      if (cartConfig) await miniAppService.saveCartPageConfig(cartConfig);
      if (profileConfig) await miniAppService.saveProfilePageConfig(profileConfig);

      notification.success({
        message: 'Xuất bản thành công!',
        description: 'Cấu hình mới đã được lưu và sẵn sàng áp dụng đồng bộ lên Zalo Mini App.',
        icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      });
    } catch (error) {
      console.error('Save error:', error);
      message.error('Lỗi khi lưu cấu hình');
    } finally {
      setSaving(false);
    }
  };

  // Drag & drop sắp xếp danh mục
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setProductsConfig((prev) => {
        const oldIndex = prev.categories.findIndex((item) => item.id === active.id);
        const newIndex = prev.categories.findIndex((item) => item.id === over.id);
        const reordered = arrayMove(prev.categories, oldIndex, newIndex).map((cat, idx) => ({
          ...cat,
          displayOrder: idx + 1,
        }));
        return {
          ...prev,
          categories: reordered,
        };
      });
    }
  };

  // Bật/tắt nhanh danh mục
  const handleToggleCategoryActive = (id: string, checked: boolean) => {
    setProductsConfig((prev) => ({
      ...prev,
      categories: prev.categories.map((cat) => (cat.id === id ? { ...cat, isActive: checked } : cat)),
    }));
  };

  // Tải ảnh danh mục lên Firebase Storage
  const handleUploadCategoryPhoto = async (file: File, formInstance: any) => {
    setUploadingImage(true);
    try {
      const storageRef = ref(storage, `miniapp-categories/${Date.now()}_${file.name}`);
      const uploadResult = await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(uploadResult.ref);
      formInstance.setFieldsValue({ imageUrl: downloadUrl });
      message.success('Đã tải ảnh lên thành công!');
    } catch (error) {
      console.error('Error uploading category image:', error);
      message.error('Lỗi khi tải ảnh lên');
    } finally {
      setUploadingImage(false);
    }
    return false;
  };

  // Mở modal sửa chi tiết danh mục
  const handleOpenEditCategory = (cat: MiniAppCategory) => {
    setEditingCategory(cat);
    categoryForm.setFieldsValue({
      name: cat.name,
      subtitle: cat.subtitle,
      sourceType: cat.sourceType || 'cukcuk',
      cukcukGroupName: cat.cukcukGroupName || cat.name,
      badge: cat.badge,
      iconName: cat.iconName || 'CoffeeOutlined',
      colorTheme: cat.colorTheme || '#8B4513',
      gradient: cat.gradient || 'linear-gradient(135deg, #FFF8E7 0%, #FFE0B2 100%)',
      imageUrl: cat.imageUrl || '',
      displayLayout: cat.displayLayout || 'horizontal_scroll',
      showInMainCategories: cat.showInMainCategories ?? true,
      showAsFeaturedSection: cat.showAsFeaturedSection ?? true,
      isActive: cat.isActive,
    });
    setEditCategoryModalVisible(true);
  };

  // Lưu chỉnh sửa danh mục từ Modal
  const handleSaveCategoryModal = () => {
    categoryForm.validateFields().then((values) => {
      if (!editingCategory) return;
      setProductsConfig((prev) => ({
        ...prev,
        categories: prev.categories.map((cat) =>
          cat.id === editingCategory.id
            ? {
                ...cat,
                ...values,
                imageUrl: values.imageUrl ? values.imageUrl.trim() : undefined,
                iconName: values.iconName || cat.iconName || 'CoffeeOutlined',
              }
            : cat
        ),
      }));
      setEditCategoryModalVisible(false);
      message.success(`Đã cập nhật danh mục "${values.name}"`);
    });
  };

  // Bật/tắt hiển thị ở dải Danh mục trên cùng
  const handleToggleMainCategory = (catId: string, show: boolean) => {
    setProductsConfig((prev) => ({
      ...prev,
      categories: prev.categories.map((c) =>
        c.id === catId ? { ...c, showInMainCategories: show } : c
      ),
    }));
  };

  // Bật/tắt hiển thị thành Khối Section bên dưới
  const handleToggleFeaturedSection = (catId: string, show: boolean) => {
    setProductsConfig((prev) => ({
      ...prev,
      categories: prev.categories.map((c) =>
        c.id === catId ? { ...c, showAsFeaturedSection: show } : c
      ),
    }));
  };

  // Xóa danh mục
  const handleDeleteCategory = (id: string) => {
    setProductsConfig((prev) => ({
      ...prev,
      categories: prev.categories.filter((c) => c.id !== id),
    }));
    message.success('Đã xóa danh mục / section');
  };

  // Mở drawer quản lý món trong danh mục
  const handleOpenManageDishes = (cat: MiniAppCategory) => {
    setSelectedCategoryForDishes(cat);
    setDishSearchTerm('');
    setManageDishesDrawerVisible(true);
  };

  // Bật/tắt món ăn trong danh mục
  const handleToggleDishExclusion = (dishId: string) => {
    if (!selectedCategoryForDishes) return;
    const catId = selectedCategoryForDishes.id;
    const currentExcluded = selectedCategoryForDishes.excludedProductIds || [];
    const isExcluded = currentExcluded.includes(dishId);

    const updatedExcluded = isExcluded
      ? currentExcluded.filter((id) => id !== dishId)
      : [...currentExcluded, dishId];

    setProductsConfig((prev) => ({
      ...prev,
      categories: prev.categories.map((cat) =>
        cat.id === catId ? { ...cat, excludedProductIds: updatedExcluded } : cat
      ),
    }));

    setSelectedCategoryForDishes((prev) =>
      prev ? { ...prev, excludedProductIds: updatedExcluded } : null
    );
  };

  // Bật/tắt món nổi bật (Featured ⭐)
  const handleToggleDishFeatured = (dishId: string) => {
    if (!selectedCategoryForDishes) return;
    const catId = selectedCategoryForDishes.id;
    const currentFeatured = selectedCategoryForDishes.featuredProductIds || [];
    const isFeatured = currentFeatured.includes(dishId);

    const updatedFeatured = isFeatured
      ? currentFeatured.filter((id) => id !== dishId)
      : [...currentFeatured, dishId];

    setProductsConfig((prev) => ({
      ...prev,
      categories: prev.categories.map((cat) =>
        cat.id === catId ? { ...cat, featuredProductIds: updatedFeatured } : cat
      ),
    }));

    setSelectedCategoryForDishes((prev) =>
      prev ? { ...prev, featuredProductIds: updatedFeatured } : null
    );
  };

  // Thêm danh mục mới
  const handleAddNewCategory = () => {
    addCategoryForm.validateFields().then((values) => {
      const newCat: MiniAppCategory = {
        id: `${values.sourceType || 'cukcuk'}_${values.code.toLowerCase().replace(/[^a-z0-9]/g, '_')}_${Date.now()}`,
        code: values.code,
        name: values.name,
        subtitle: values.subtitle,
        sourceType: values.sourceType || 'cukcuk',
        cukcukGroupName: values.cukcukGroupName || values.name,
        badge: values.badge,
        iconName: values.iconName || 'CoffeeOutlined',
        colorTheme: values.colorTheme || '#C67C4E',
        gradient: values.gradient || 'linear-gradient(135deg, #FFF3E0 0%, #FFE082 100%)',
        imageUrl: values.imageUrl ? values.imageUrl.trim() : undefined,
        displayOrder: productsConfig.categories.length + 1,
        isActive: true,
        displayLayout: 'horizontal_scroll',
        showInMainCategories: values.showInMainCategories !== false,
        showAsFeaturedSection: values.showAsFeaturedSection !== false,
      };

      setProductsConfig((prev) => ({
        ...prev,
        categories: [...prev.categories, newCat],
      }));

      addCategoryForm.resetFields();
      setAddCategoryModalVisible(false);
      message.success(`Đã thêm danh mục/khối mới: ${values.name}`);
    });
  };

  // Các nhóm Cukcuk chưa được thêm vào config
  const unusedCukcukGroups = useMemo(() => {
    const existingGroupNames = new Set(
      productsConfig.categories.map((c) => (c.cukcukGroupName || c.name).toLowerCase())
    );
    return cukcukGroups.filter((g) => !existingGroupNames.has(g.groupName.toLowerCase()));
  }, [cukcukGroups, productsConfig.categories]);

  // Danh sách món / sản phẩm của danh mục đang chọn trong Drawer
  const currentCategoryDishes = useMemo(() => {
    if (!selectedCategoryForDishes) return [];
    const cat = selectedCategoryForDishes;
    const term = dishSearchTerm.toLowerCase().trim();

    let items: Array<{ id: string; name: string; code?: string; price: number; imageUrl?: string }> = [];

    if (cat.sourceType === 'coffee') {
      items = coffeeList.map((c: any) => ({
        id: c.id || '',
        name: c.name || '',
        code: c.code || '',
        price: c.price || c.weightAndPrice?.[0]?.price || 0,
        imageUrl: c.images?.[0] || c.imageUrl,
      }));
    } else if (cat.sourceType === 'bottledDrink') {
      items = bottledDrinkList.map((d: any) => ({
        id: d.id || '',
        name: d.product_name || '',
        code: d.id || '',
        price: d.volumes?.[0]?.price || d.price || 0,
        imageUrl: d.images?.[0] || d.imageUrl,
      }));
    } else if (cat.sourceType === 'equipment') {
      items = equipmentList.map((e: any) => ({
        id: e.id || '',
        name: e.name || e.categoryName || 'Dụng cụ cà phê',
        code: e.id || '',
        price: e.price || 0,
        imageUrl: e.images?.[0] || e.imageUrl,
      }));
    } else if (cat.sourceType === 'news' || cat.code === 'news') {
      items = newsMessageList.map((m: any) => ({
        id: m.id || '',
        name: m.template_data?.header?.content || 'Bản tin',
        code: m.id || '',
        price: 0,
        imageUrl: m.template_data?.banner?.image_url,
      }));
    } else {
      // CUKCUK
      const groupName = (cat.cukcukGroupName || cat.name).toLowerCase();
      const group = cukcukGroups.find(
        (g) =>
          g.groupName.toLowerCase() === groupName ||
          g.groupCode.toLowerCase() === cat.code.toLowerCase()
      );
      items = (group?.dishes || []).map((d) => ({
        id: d.id,
        name: d.name,
        code: d.code,
        price: d.price || 0,
        imageUrl: d.imageUrl,
      }));
    }

    if (!term) return items;
    return items.filter(
      (item) => item.name.toLowerCase().includes(term) || (item.code && item.code.toLowerCase().includes(term))
    );
  }, [selectedCategoryForDishes, cukcukGroups, coffeeList, bottledDrinkList, equipmentList, newsMessageList, dishSearchTerm]);

  // Cột cho Bảng Danh mục & Khối Section
  const categoryColumns: ColumnsType<MiniAppCategory> = [
    {
      key: 'sort',
      width: 36,
      align: 'center',
      render: () => null,
    },
    {
      title: 'Thứ tự',
      dataIndex: 'displayOrder',
      key: 'displayOrder',
      width: 65,
      align: 'center',
      render: (val: number) => <Tag color="blue">{val}</Tag>,
    },
    {
      title: 'Ảnh / Icon',
      key: 'visual',
      width: 80,
      render: (_, record) => (
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            background: record.gradient || '#f3f4f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            border: `1px solid ${record.colorTheme || '#e5e7eb'}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }}
        >
          {record.imageUrl ? (
            <img
              src={record.imageUrl}
              alt={record.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            renderCategoryIcon(record.iconName, { fontSize: 20, color: record.colorTheme || '#6b7280' })
          )}
        </div>
      ),
    },
    {
      title: 'Tên danh mục / Section',
      key: 'name',
      render: (_, record) => (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: '#1f2937' }}>{record.name}</span>
            {record.badge && (
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 800,
                  padding: '1px 5px',
                  borderRadius: 4,
                  backgroundColor: '#fee2e2',
                  color: '#dc2626',
                }}
              >
                {record.badge}
              </span>
            )}
          </div>
          {record.subtitle && (
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{record.subtitle}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Nguồn',
      key: 'sourceType',
      width: 130,
      render: (_, record) => {
        switch (record.sourceType) {
          case 'coffee':
            return <Tag color="gold">🏪 Hạt cà phê</Tag>;
          case 'bottledDrink':
            return <Tag color="cyan">🛍️ Đóng chai</Tag>;
          case 'equipment':
            return <Tag color="purple">🛠️ Dụng cụ</Tag>;
          case 'news':
            return <Tag color="magenta">📰 Tin tức & Ưu đãi</Tag>;
          case 'cukcuk':
          default:
            return <Tag color="blue">☕ CUKCUK</Tag>;
        }
      },
    },
    {
      title: 'Số lượng',
      key: 'dishCount',
      width: 90,
      align: 'center',
      render: (_, record) => {
        const excludedCount = record.excludedProductIds?.length || 0;
        const totalCount = record.itemCount || 0;
        const activeCount = Math.max(0, totalCount - excludedCount);
        const unit = record.sourceType === 'news' ? 'bài' : 'món';
        return (
          <Tooltip title={`Hiển thị ${activeCount} trên tổng số ${totalCount} ${unit}`}>
            <span style={{ fontWeight: 600, fontSize: 12, color: activeCount > 0 ? '#15803d' : '#9ca3af' }}>
              {activeCount}/{totalCount} {unit}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: (
        <Tooltip title="Hiển thị icon/ảnh ở dải danh mục cuộn ngang trên cùng">
          <span>Dải trên</span>
        </Tooltip>
      ),
      key: 'showInMainCategories',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <Switch
          size="small"
          checked={record.showInMainCategories !== false}
          onChange={(checked) => handleToggleMainCategory(record.id, checked)}
        />
      ),
    },
    {
      title: (
        <Tooltip title="Hiển thị thành khối section cuộn sản phẩm bên dưới">
          <span>Khối dưới</span>
        </Tooltip>
      ),
      key: 'showAsFeaturedSection',
      width: 85,
      align: 'center',
      render: (_, record) => (
        <Switch
          size="small"
          checked={record.showAsFeaturedSection !== false}
          onChange={(checked) => handleToggleFeaturedSection(record.id, checked)}
        />
      ),
    },
    {
      title: 'Bật/Tắt',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 75,
      align: 'center',
      render: (isActive: boolean, record) => (
        <Switch
          size="small"
          checked={isActive}
          onChange={(checked) => handleToggleCategoryActive(record.id, checked)}
          checkedChildren={<CheckOutlined />}
        />
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 150,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Quản lý món trong danh mục">
            <Button
              type="text"
              size="small"
              icon={<AppstoreOutlined style={{ color: '#2563eb' }} />}
              onClick={() => handleOpenManageDishes(record)}
            />
          </Tooltip>
          <Tooltip title="Chỉnh sửa danh mục">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined style={{ color: '#059669' }} />}
              onClick={() => handleOpenEditCategory(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Xóa danh mục này khỏi Mini App?"
            onConfirm={() => handleDeleteCategory(record.id)}
            okText="Xóa"
            cancelText="Hủy"
            okType="danger"
          >
            <Tooltip title="Xóa">
              <Button type="text" size="small" icon={<DeleteOutlined style={{ color: '#dc2626' }} />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Chuyển tab đồng bộ giữa Admin và Phone Simulator
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setSimulatorActiveTab(key);
  };

  return (
    <AdminLayout>
      <ConfigProvider locale={viVN}>
        <div className="miniapp-mgmt-container">
          {/* Header Bar */}
          <div className="miniapp-header-bar">
            <div className="miniapp-header-info">
              <h2>
                <MobileOutlined style={{ color: '#ea580c' }} /> Quản lý Zalo Mini App
              </h2>
              <p>Tùy chỉnh giao diện, danh mục CUKCUK và nội dung hiển thị trên Zalo Mini App mà không cần xét duyệt</p>
            </div>
            <div className="miniapp-action-buttons">
              <Button
                icon={<SyncOutlined spin={syncing} />}
                onClick={handleSyncFromCukcuk}
                loading={syncing}
                style={{ borderRadius: 8 }}
              >
                Đồng bộ từ CUKCUK
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleResetDefaults}
                style={{ borderRadius: 8 }}
              >
                Khôi phục mặc định
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSaveAndPublish}
                loading={saving}
                style={{ backgroundColor: '#ea580c', borderColor: '#ea580c', borderRadius: 8 }}
              >
                Lưu & Xuất bản lên Mini App
              </Button>
              <Button
                type={showSimulator ? 'primary' : 'default'}
                icon={<EyeOutlined />}
                onClick={() => setShowSimulator(!showSimulator)}
                style={{ borderRadius: 8 }}
              >
                {showSimulator ? 'Ẩn Mô phỏng' : 'Xem Mô phỏng'}
              </Button>
            </div>
          </div>

          {/* Main Layout (Config on Left, Phone Simulator on Right) */}
          <div className="miniapp-main-layout">
            {/* Cột cấu hình */}
            <div className="miniapp-config-column">
              <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                <Tabs activeKey={activeTab} onChange={handleTabChange} size="large" type="card">
                  {/* TAB 1: TRANG SẢN PHẨM */}
                  <TabPane
                    tab={
                      <span>
                        <CoffeeOutlined /> Trang Sản phẩm
                      </span>
                    }
                    key="products"
                  >
                    {/* Thống kê nhanh */}
                    <Row gutter={16} style={{ marginBottom: 20 }}>
                      <Col xs={24} sm={8}>
                        <Card size="small" style={{ borderRadius: 8, background: '#f8fafc' }}>
                          <Statistic
                            title="Tổng danh mục"
                            value={productsConfig.categories.length}
                            suffix="danh mục"
                            valueStyle={{ color: '#1e293b', fontWeight: 700 }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Card size="small" style={{ borderRadius: 8, background: '#f0fdf4' }}>
                          <Statistic
                            title="Danh mục đang bật"
                            value={productsConfig.categories.filter((c) => c.isActive).length}
                            suffix={`/ ${productsConfig.categories.length}`}
                            valueStyle={{ color: '#16a34a', fontWeight: 700 }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Card size="small" style={{ borderRadius: 8, background: '#eff6ff' }}>
                          <Statistic
                            title="Nhóm món CUKCUK quét được"
                            value={cukcukGroups.length}
                            suffix="nhóm"
                            valueStyle={{ color: '#2563eb', fontWeight: 700 }}
                          />
                        </Card>
                      </Col>
                    </Row>

                    {/* Cấu hình chung của trang Sản phẩm */}
                    <div style={{ background: '#fafafa', padding: 16, borderRadius: 8, marginBottom: 20 }}>
                      <Title level={5} style={{ margin: '0 0 12px 0', color: '#374151' }}>
                        ⚙️ Cấu hình tiêu đề & hiển thị chung
                      </Title>
                      <Row gutter={16}>
                        <Col xs={24} md={12}>
                          <Form.Item label="Tiêu đề trang sản phẩm" style={{ marginBottom: 12 }}>
                            <Input
                              value={productsConfig.headerTitle}
                              onChange={(e) =>
                                setProductsConfig((prev) => ({ ...prev, headerTitle: e.target.value }))
                              }
                              placeholder="Ví dụ: Sản Phẩm"
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                          <Form.Item label="Tiêu đề thanh danh mục" style={{ marginBottom: 12 }}>
                            <Input
                              value={productsConfig.categorySectionTitle}
                              onChange={(e) =>
                                setProductsConfig((prev) => ({
                                  ...prev,
                                  categorySectionTitle: e.target.value,
                                }))
                              }
                              placeholder="Ví dụ: Danh mục"
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={6}>
                          <Form.Item label="Thanh tìm kiếm" style={{ marginBottom: 0 }}>
                            <Switch
                              checked={productsConfig.showSearchBar}
                              onChange={(checked) =>
                                setProductsConfig((prev) => ({ ...prev, showSearchBar: checked }))
                              }
                              checkedChildren="Bật"
                              unCheckedChildren="Tắt"
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={6}>
                          <Form.Item label="Đổi cửa hàng" style={{ marginBottom: 0 }}>
                            <Switch
                              checked={productsConfig.showStoreSelector}
                              onChange={(checked) =>
                                setProductsConfig((prev) => ({ ...prev, showStoreSelector: checked }))
                              }
                              checkedChildren="Bật"
                              unCheckedChildren="Tắt"
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>

                    {/* Danh sách danh mục CUKCUK */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <div>
                        <Title level={5} style={{ margin: 0, color: '#111827' }}>
                          📂 Danh mục sản phẩm (Lấy từ CUKCUK Menu)
                        </Title>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                          Kéo thả biểu tượng ⠿ ở đầu dòng để sắp xếp thứ tự hiển thị trên Mini App
                        </Text>
                      </div>
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setAddCategoryModalVisible(true)}
                        style={{ borderRadius: 8, backgroundColor: '#0284c7', borderColor: '#0284c7' }}
                      >
                        Thêm danh mục
                      </Button>
                    </div>

                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext
                        items={productsConfig.categories.map((c) => c.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <Table
                          components={{
                            body: {
                              row: DraggableRow,
                            },
                          }}
                          rowKey="id"
                          columns={categoryColumns}
                          dataSource={productsConfig.categories}
                          pagination={false}
                          loading={loading}
                          size="small"
                          bordered
                          style={{ borderRadius: 8, overflow: 'hidden' }}
                        />
                      </SortableContext>
                    </DndContext>
                  </TabPane>

                  {/* TAB 2: TRANG CHO BẠN */}
                  <TabPane
                    tab={
                      <span>
                        <HomeOutlined /> Trang Cho bạn
                      </span>
                    }
                    key="foryou"
                  >
                    <div style={{ padding: '8px 0' }}>
                      <Title level={5}>Cấu hình Trang Cho bạn (Trang chủ Mini App)</Title>
                      <Paragraph type="secondary">
                        Tùy chỉnh thông điệp chào mừng và bật/tắt các khối nội dung trên trang chính của ứng dụng.
                      </Paragraph>
                      {forYouConfig && (
                        <Form layout="vertical">
                          <Row gutter={16}>
                            <Col xs={24} md={12}>
                              <Form.Item label="Tiêu đề trang">
                                <Input
                                  value={forYouConfig.headerTitle}
                                  onChange={(e) =>
                                    setForYouConfig({ ...forYouConfig, headerTitle: e.target.value })
                                  }
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                              <Form.Item label="Lời chào khách hàng">
                                <Input
                                  value={forYouConfig.welcomeMessage}
                                  onChange={(e) =>
                                    setForYouConfig({ ...forYouConfig, welcomeMessage: e.target.value })
                                  }
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Divider orientation="left">Các khối nội dung hiển thị</Divider>
                          <Table
                            dataSource={forYouConfig.sections}
                            rowKey="id"
                            pagination={false}
                            columns={[
                              { title: 'Tên khối', dataIndex: 'title', key: 'title', render: (t) => <strong>{t}</strong> },
                              { title: 'Loại khối', dataIndex: 'type', key: 'type', render: (t) => <Tag color="cyan">{t}</Tag> },
                              {
                                title: 'Hiển thị',
                                dataIndex: 'isActive',
                                key: 'isActive',
                                align: 'center',
                                render: (active, record) => (
                                  <Switch
                                    checked={active}
                                    onChange={(checked) =>
                                      setForYouConfig({
                                        ...forYouConfig,
                                        sections: forYouConfig.sections.map((s) =>
                                          s.id === record.id ? { ...s, isActive: checked } : s
                                        ),
                                      })
                                    }
                                  />
                                ),
                              },
                            ]}
                          />
                        </Form>
                      )}
                    </div>
                  </TabPane>

                  {/* TAB 3: TRANG TÌM KIẾM */}
                  <TabPane
                    tab={
                      <span>
                        <SearchOutlined /> Trang Tìm kiếm
                      </span>
                    }
                    key="search"
                  >
                    <div style={{ padding: '8px 0' }}>
                      <Title level={5}>Cấu hình Trang Tìm kiếm</Title>
                      <Paragraph type="secondary">
                        Cấu hình từ khóa thịnh hành (Trending), gợi ý nhanh và các tính năng tìm kiếm.
                      </Paragraph>
                      {searchConfig && (
                        <Form layout="vertical">
                          <Row gutter={16}>
                            <Col xs={24} md={12}>
                              <Form.Item label="Placeholder ô tìm kiếm">
                                <Input
                                  value={searchConfig.searchPlaceholder}
                                  onChange={(e) =>
                                    setSearchConfig({ ...searchConfig, searchPlaceholder: e.target.value })
                                  }
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                              <Form.Item label="Tìm kiếm bằng giọng nói">
                                <Switch
                                  checked={searchConfig.enableVoiceSearch}
                                  onChange={(checked) =>
                                    setSearchConfig({ ...searchConfig, enableVoiceSearch: checked })
                                  }
                                  checkedChildren="Bật"
                                  unCheckedChildren="Tắt"
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Form.Item label="Từ khóa tìm kiếm thịnh hành (Trending)">
                            <Select
                              mode="tags"
                              style={{ width: '100%' }}
                              value={searchConfig.trendingSearches}
                              onChange={(tags) => setSearchConfig({ ...searchConfig, trendingSearches: tags })}
                              placeholder="Nhập từ khóa và ấn Enter"
                            />
                          </Form.Item>
                          <Form.Item label="Thẻ gợi ý tìm kiếm nhanh (Suggested Tags)">
                            <Select
                              mode="tags"
                              style={{ width: '100%' }}
                              value={searchConfig.suggestedTags}
                              onChange={(tags) => setSearchConfig({ ...searchConfig, suggestedTags: tags })}
                              placeholder="Nhập tag và ấn Enter"
                            />
                          </Form.Item>
                        </Form>
                      )}
                    </div>
                  </TabPane>

                  {/* TAB 4: TRANG GIỎ HÀNG */}
                  <TabPane
                    tab={
                      <span>
                        <ShoppingCartOutlined /> Trang Giỏ hàng
                      </span>
                    }
                    key="cart"
                  >
                    <div style={{ padding: '8px 0' }}>
                      <Title level={5}>Cấu hình Trang Giỏ hàng</Title>
                      <Paragraph type="secondary">
                        Cấu hình các gợi ý ghi chú mẫu khi khách đặt hàng, ngưỡng miễn phí vận chuyển,...
                      </Paragraph>
                      {cartConfig && (
                        <Form layout="vertical">
                          <Row gutter={16}>
                            <Col xs={24} md={12}>
                              <Form.Item label="Ngưỡng thông báo miễn phí ship (VNĐ)">
                                <InputNumber
                                  style={{ width: '100%' }}
                                  value={cartConfig.freeShippingNoticeThreshold}
                                  formatter={(val) => `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                  parser={(val) => Number(val?.replace(/\$\s?|(,*)/g, '') || 0)}
                                  onChange={(val) =>
                                    setCartConfig({ ...cartConfig, freeShippingNoticeThreshold: val || 0 })
                                  }
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                          <Form.Item label="Gợi ý ghi chú cho khách hàng chọn nhanh">
                            <Select
                              mode="tags"
                              style={{ width: '100%' }}
                              value={cartConfig.suggestedNotes}
                              onChange={(notes) => setCartConfig({ ...cartConfig, suggestedNotes: notes })}
                              placeholder="Nhập ghi chú mẫu và ấn Enter (VD: Ít đá, Không đường...)"
                            />
                          </Form.Item>
                        </Form>
                      )}
                    </div>
                  </TabPane>

                  {/* TAB 5: TRANG CÁ NHÂN */}
                  <TabPane
                    tab={
                      <span>
                        <UserOutlined /> Trang Cá nhân
                      </span>
                    }
                    key="profile"
                  >
                    <div style={{ padding: '8px 0' }}>
                      <Title level={5}>Cấu hình Trang Cá nhân & Hội viên</Title>
                      <Paragraph type="secondary">
                        Cấu hình thông tin liên hệ, hotline hỗ trợ và các menu chức năng tài khoản.
                      </Paragraph>
                      {profileConfig && (
                        <Form layout="vertical">
                          <Row gutter={16}>
                            <Col xs={24} md={12}>
                              <Form.Item label="Hotline hỗ trợ">
                                <Input
                                  value={profileConfig.hotline}
                                  onChange={(e) =>
                                    setProfileConfig({ ...profileConfig, hotline: e.target.value })
                                  }
                                />
                              </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                              <Form.Item label="Địa chỉ thương hiệu">
                                <Input
                                  value={profileConfig.address}
                                  onChange={(e) =>
                                    setProfileConfig({ ...profileConfig, address: e.target.value })
                                  }
                                />
                              </Form.Item>
                            </Col>
                          </Row>
                        </Form>
                      )}
                    </div>
                  </TabPane>
                </Tabs>
              </Card>
            </div>

            {/* Cột mô phỏng điện thoại trực tiếp */}
            {showSimulator && (
              <div className="miniapp-preview-column">
                <div className="phone-simulator-wrapper">
                  <div className="phone-simulator-title">
                    <span>
                      <MobileOutlined style={{ color: '#ea580c', marginRight: 6 }} /> Xem trước Zalo Mini App
                    </span>
                    <Badge status="processing" text="Live Preview" />
                  </div>

                  <div className="phone-frame">
                    <div className="phone-notch">
                      <div className="phone-notch-speaker"></div>
                      <div className="phone-notch-camera"></div>
                    </div>

                    <div className="phone-screen">
                      {/* Status bar */}
                      <div className="phone-status-bar">
                        <span>9:41</span>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          <span style={{ fontSize: 9 }}>5G</span>
                          <div style={{ width: 16, height: 8, border: '1px solid #374151', borderRadius: 2, padding: 1 }}>
                            <div style={{ width: '80%', height: '100%', background: '#374151' }}></div>
                          </div>
                        </div>
                      </div>

                      {/* Header Mini App */}
                      <div className="phone-app-header">
                        <div>
                          <div className="phone-app-brand">8am Coffee</div>
                          <div style={{ fontSize: 10, color: '#ea580c', fontWeight: 600 }}>📍 8am Roastery Space</div>
                        </div>
                        <div className="phone-zalo-controls">
                          <span>•••</span>
                          <span>|</span>
                          <span>✕</span>
                        </div>
                      </div>

                      {/* Content Scrollable */}
                      <div className="phone-content-scroll">
                        {simulatorActiveTab === 'products' && (
                          <div style={{ padding: '12px 10px' }}>
                            {/* Search bar in phone */}
                            {productsConfig.showSearchBar && (
                              <div
                                style={{
                                  background: '#f3f4f6',
                                  borderRadius: 18,
                                  padding: '6px 12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  marginBottom: 12,
                                  fontSize: 11,
                                  color: '#9ca3af',
                                }}
                              >
                                <SearchOutlined />
                                <span>Tìm kiếm cà phê, món uống...</span>
                              </div>
                            )}

                            {/* Section Danh mục */}
                            <div style={{ marginBottom: 16 }}>
                              <div style={{ fontSize: 13, fontWeight: 800, color: '#111827', marginBottom: 8 }}>
                                {productsConfig.categorySectionTitle || 'Danh mục'}
                              </div>

                              {/* Horizontal scroll pills */}
                              <div
                                style={{
                                  display: 'flex',
                                  overflowX: 'auto',
                                  gap: 8,
                                  paddingBottom: 6,
                                }}
                              >
                                {productsConfig.categories
                                  .filter((c) => c.isActive && c.showInMainCategories !== false)
                                  .map((cat) => (
                                    <div
                                      key={cat.id}
                                      className="phone-cat-pill"
                                      style={{
                                        background: cat.gradient || '#f8fafc',
                                        border: `1px solid ${cat.colorTheme || '#e2e8f0'}`,
                                      }}
                                      onClick={() => setSimulatorSelectedCat(cat.id)}
                                    >
                                      <div className="phone-cat-icon-box">
                                        {cat.imageUrl ? (
                                          <img src={cat.imageUrl} alt={cat.name} />
                                        ) : (
                                          renderCategoryIcon(cat.iconName, { color: cat.colorTheme || '#475569', fontSize: 18 })
                                        )}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: 11,
                                          fontWeight: 700,
                                          color: '#1f2937',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          width: '100%',
                                        }}
                                      >
                                        {cat.name}
                                      </div>
                                      {cat.badge && (
                                        <span
                                          style={{
                                            fontSize: 8,
                                            fontWeight: 800,
                                            background: '#ef4444',
                                            color: '#fff',
                                            borderRadius: 4,
                                            padding: '1px 3px',
                                            marginTop: 2,
                                          }}
                                        >
                                          {cat.badge}
                                        </span>
                                      )}
                                    </div>
                                  ))}
                              </div>
                            </div>

                            {/* Sections sản phẩm đa nguồn cuộn bên dưới */}
                            {productsConfig.categories
                              .filter((c) => c.isActive && c.showAsFeaturedSection !== false)
                              .map((cat) => {
                                const excludedIds = new Set(cat.excludedProductIds || []);
                                const featuredIds = new Set(cat.featuredProductIds || []);

                                // Xử lý section Tin tức & Ưu đãi
                                if (cat.sourceType === 'news' || cat.code === 'news') {
                                  const newsItems = newsMessageList.filter((m: any) => !excludedIds.has(m.id));
                                  if (newsItems.length === 0) return null;

                                  return (
                                    <div key={cat.id} style={{ marginBottom: 16 }}>
                                      <div
                                        style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          marginBottom: 8,
                                        }}
                                      >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                          <span style={{ fontSize: 12, fontWeight: 700, color: '#1f2937' }}>
                                            {cat.name} ({newsItems.length})
                                          </span>
                                          {cat.badge && (
                                            <span style={{ fontSize: 8, background: '#ede9fe', color: '#7c3aed', padding: '1px 4px', borderRadius: 3, fontWeight: 800 }}>
                                              {cat.badge}
                                            </span>
                                          )}
                                        </div>
                                        <span style={{ fontSize: 10, color: cat.colorTheme || '#7c3aed', fontWeight: 600 }}>
                                          Xem thêm &gt;
                                        </span>
                                      </div>

                                      <div style={{ display: 'flex', overflowX: 'auto', gap: 8, paddingBottom: 4 }}>
                                        {newsItems.slice(0, 4).map((msg: any) => (
                                          <div key={msg.id} className="phone-product-card-mini" style={{ width: 110 }}>
                                            <div style={{ height: 60, background: '#f3f4f6', overflow: 'hidden' }}>
                                              {msg.template_data?.banner?.image_url ? (
                                                <img
                                                  src={msg.template_data.banner.image_url}
                                                  alt="banner"
                                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                              ) : (
                                                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af' }}>
                                                  <PictureOutlined />
                                                </div>
                                              )}
                                            </div>
                                            <div style={{ padding: 6 }}>
                                              <div
                                                style={{
                                                  fontSize: 10,
                                                  fontWeight: 700,
                                                  color: '#111827',
                                                  whiteSpace: 'nowrap',
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                                }}
                                              >
                                                {msg.template_data?.header?.content || 'Tin tức'}
                                              </div>
                                              <div style={{ fontSize: 9, color: '#6b7280', marginTop: 2 }}>
                                                Bản tin mới
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                }

                                // Xử lý các section sản phẩm: CUKCUK, Cà phê hạt, Đóng chai, Dụng cụ
                                let items: Array<{ id: string; name: string; price: number; imageUrl?: string }> = [];

                                if (cat.sourceType === 'coffee') {
                                  items = coffeeList
                                    .filter((c: any) => !excludedIds.has(c.id))
                                    .map((c: any) => ({
                                      id: c.id,
                                      name: c.name,
                                      price: c.price || c.weightAndPrice?.[0]?.price || 0,
                                      imageUrl: c.images?.[0] || c.imageUrl,
                                    }));
                                } else if (cat.sourceType === 'bottledDrink') {
                                  items = bottledDrinkList
                                    .filter((d: any) => !excludedIds.has(d.id))
                                    .map((d: any) => ({
                                      id: d.id,
                                      name: d.product_name,
                                      price: d.volumes?.[0]?.price || d.price || 0,
                                      imageUrl: d.images?.[0] || d.imageUrl,
                                    }));
                                } else if (cat.sourceType === 'equipment') {
                                  items = equipmentList
                                    .filter((e: any) => !excludedIds.has(e.id))
                                    .map((e: any) => ({
                                      id: e.id,
                                      name: e.name || e.categoryName,
                                      price: e.price || 0,
                                      imageUrl: e.images?.[0] || e.imageUrl,
                                    }));
                                } else {
                                  // CUKCUK
                                  const matchingGroup = cukcukGroups.find(
                                    (g) =>
                                      g.groupName.toLowerCase() === (cat.cukcukGroupName || cat.name).toLowerCase() ||
                                      g.groupCode.toLowerCase() === cat.code.toLowerCase()
                                  );
                                  items = (matchingGroup?.dishes || [])
                                    .filter((d) => !excludedIds.has(d.id))
                                    .map((d) => ({
                                      id: d.id,
                                      name: d.name,
                                      price: d.price || 0,
                                      imageUrl: d.imageUrl,
                                    }));
                                }

                                // Ưu tiên món nổi bật lên đầu
                                const sortedItems = [...items].sort((a, b) => {
                                  const aFeat = featuredIds.has(a.id) ? 1 : 0;
                                  const bFeat = featuredIds.has(b.id) ? 1 : 0;
                                  return bFeat - aFeat;
                                });

                                return (
                                  <div key={cat.id} style={{ marginBottom: 16 }}>
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        marginBottom: 8,
                                      }}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: '#1f2937' }}>
                                          {cat.name} ({sortedItems.length})
                                        </span>
                                        {cat.badge && (
                                          <span style={{ fontSize: 8, background: '#fee2e2', color: '#dc2626', padding: '1px 4px', borderRadius: 3, fontWeight: 800 }}>
                                            {cat.badge}
                                          </span>
                                        )}
                                      </div>
                                      <span style={{ fontSize: 10, color: cat.colorTheme || '#ea580c', fontWeight: 600 }}>
                                        Xem thêm &gt;
                                      </span>
                                    </div>

                                    {/* Món / Sản phẩm cuộn ngang */}
                                    <div style={{ display: 'flex', overflowX: 'auto', gap: 8, paddingBottom: 4 }}>
                                      {sortedItems.length > 0 ? (
                                        sortedItems.slice(0, 4).map((item) => {
                                          const isFeatured = featuredIds.has(item.id);
                                          return (
                                            <div key={item.id} className="phone-product-card-mini" style={{ position: 'relative' }}>
                                              {isFeatured && (
                                                <span
                                                  style={{
                                                    position: 'absolute',
                                                    top: 2,
                                                    left: 2,
                                                    zIndex: 2,
                                                    fontSize: 7,
                                                    fontWeight: 800,
                                                    background: '#f59e0b',
                                                    color: '#fff',
                                                    padding: '1px 3px',
                                                    borderRadius: 2,
                                                  }}
                                                >
                                                  ⭐ Nổi bật
                                                </span>
                                              )}
                                              <div style={{ height: 60, background: '#f3f4f6', overflow: 'hidden' }}>
                                                {item.imageUrl ? (
                                                  <img
                                                    src={item.imageUrl}
                                                    alt={item.name}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                  />
                                                ) : (
                                                  <div
                                                    style={{
                                                      height: '100%',
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                      color: '#9ca3af',
                                                    }}
                                                  >
                                                    <CoffeeOutlined />
                                                  </div>
                                                )}
                                              </div>
                                              <div style={{ padding: 6 }}>
                                                <div
                                                  style={{
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    color: '#111827',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                  }}
                                                >
                                                  {item.name}
                                                </div>
                                                <div style={{ fontSize: 10, color: '#ea580c', fontWeight: 800, marginTop: 2 }}>
                                                  {item.price > 0 ? `${item.price.toLocaleString()}đ` : 'Liên hệ'}
                                                </div>
                                              </div>
                                            </div>
                                          );
                                        })
                                      ) : (
                                        <div
                                          style={{
                                            fontSize: 10,
                                            color: '#9ca3af',
                                            padding: 8,
                                            background: '#f9fafb',
                                            borderRadius: 8,
                                            width: '100%',
                                            textAlign: 'center',
                                          }}
                                        >
                                          Chưa có sản phẩm trong nhóm này
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        )}

                        {/* PREVIEW TAB 2: CHO BẠN */}
                        {simulatorActiveTab === 'foryou' && (
                          <div style={{ padding: '12px 10px' }}>
                            {/* Welcome Banner */}
                            <div
                              style={{
                                background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
                                borderRadius: 14,
                                padding: '14px 12px',
                                color: '#ffffff',
                                marginBottom: 12,
                                boxShadow: '0 4px 12px rgba(234, 88, 12, 0.25)',
                              }}
                            >
                              <div style={{ fontSize: 13, fontWeight: 800 }}>{forYouConfig?.headerTitle || 'Cho Bạn'}</div>
                              <div style={{ fontSize: 10, opacity: 0.9, marginTop: 3 }}>
                                {forYouConfig?.welcomeMessage || 'Chào bạn! Hôm nay bạn muốn dùng cà phê gì?'}
                              </div>
                            </div>

                            {/* Render các section đang bật */}
                            {(forYouConfig?.sections || [])
                              .filter((s) => s.isActive)
                              .map((sec) => (
                                <div
                                  key={sec.id}
                                  style={{
                                    background: '#ffffff',
                                    borderRadius: 10,
                                    border: '1px solid #f3f4f6',
                                    padding: '10px 10px',
                                    marginBottom: 10,
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      marginBottom: 6,
                                    }}
                                  >
                                    <span style={{ fontSize: 11, fontWeight: 700, color: '#1f2937' }}>
                                      {sec.type === 'banner' && '📢 '}
                                      {sec.type === 'recommendations' && '⭐ '}
                                      {sec.type === 'favorites' && '❤️ '}
                                      {sec.type === 'collections' && '📦 '}
                                      {sec.type === 'gifts' && '🎁 '}
                                      {sec.type === 'history' && '🕒 '}
                                      {sec.title}
                                    </span>
                                    <span style={{ fontSize: 9, color: '#ea580c', fontWeight: 600 }}>Tất cả &gt;</span>
                                  </div>

                                  {sec.type === 'banner' ? (
                                    <div
                                      style={{
                                        height: 70,
                                        background: 'linear-gradient(135deg, #fed7aa 0%, #fdba74 100%)',
                                        borderRadius: 8,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#9a3412',
                                        fontSize: 11,
                                        fontWeight: 700,
                                      }}
                                    >
                                      Banner ưu đãi tháng
                                    </div>
                                  ) : (
                                    <div style={{ display: 'flex', gap: 6, overflowX: 'auto' }}>
                                      {[1, 2, 3].map((i) => (
                                        <div
                                          key={i}
                                          style={{
                                            flexShrink: 0,
                                            width: 80,
                                            height: 60,
                                            background: '#f9fafb',
                                            borderRadius: 6,
                                            border: '1px solid #f0f0f0',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: 4,
                                          }}
                                        >
                                          <CoffeeOutlined style={{ color: '#ea580c', fontSize: 14 }} />
                                          <span style={{ fontSize: 8, color: '#4b5563', marginTop: 3 }}>Mục #{i}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                          </div>
                        )}

                        {/* PREVIEW TAB 3: TÌM KIẾM */}
                        {simulatorActiveTab === 'search' && (
                          <div style={{ padding: '12px 10px' }}>
                            {/* Search bar */}
                            <div
                              style={{
                                background: '#f3f4f6',
                                borderRadius: 20,
                                padding: '8px 12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: 14,
                                fontSize: 11,
                                color: '#374151',
                                border: '1px solid #e5e7eb',
                              }}
                            >
                              <SearchOutlined style={{ color: '#ea580c' }} />
                              <span style={{ flex: 1, color: '#6b7280' }}>
                                {searchConfig?.searchPlaceholder || 'Tìm cà phê, món uống, dụng cụ...'}
                              </span>
                              {searchConfig?.enableVoiceSearch && (
                                <span style={{ fontSize: 10, color: '#ea580c' }}>🎙️</span>
                              )}
                            </div>

                            {/* Trending keywords */}
                            <div style={{ marginBottom: 14 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
                                🔥 Từ khóa thịnh hành
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                                {(searchConfig?.trendingSearches || []).map((term, i) => (
                                  <span
                                    key={i}
                                    style={{
                                      fontSize: 10,
                                      background: '#fff7ed',
                                      border: '1px solid #ffedd5',
                                      padding: '3px 8px',
                                      borderRadius: 12,
                                      color: '#ea580c',
                                      fontWeight: 600,
                                    }}
                                  >
                                    {term}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Suggested tags */}
                            <div style={{ marginBottom: 14 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
                                ✨ Gợi ý nhanh
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                                {(searchConfig?.suggestedTags || []).map((tag, i) => (
                                  <span
                                    key={i}
                                    style={{
                                      fontSize: 10,
                                      background: '#f3f4f6',
                                      padding: '3px 8px',
                                      borderRadius: 12,
                                      color: '#4b5563',
                                    }}
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Recent Searches */}
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
                                🕒 Tìm kiếm gần đây (Tối đa {searchConfig?.maxRecentSearches || 10})
                              </div>
                              {['Specialty Pour Over', 'Cold Brew Đào'].map((term, i) => (
                                <div
                                  key={i}
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    padding: '5px 0',
                                    borderBottom: '1px dashed #f0f0f0',
                                    fontSize: 10,
                                    color: '#6b7280',
                                  }}
                                >
                                  <span>{term}</span>
                                  <span>✕</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* PREVIEW TAB 4: GIỎ HÀNG */}
                        {simulatorActiveTab === 'cart' && (
                          <div style={{ padding: '12px 10px' }}>
                            {/* Free Shipping Alert */}
                            {cartConfig && cartConfig.freeShippingNoticeThreshold > 0 && (
                              <div
                                style={{
                                  background: '#ecfdf5',
                                  border: '1px solid #a7f3d0',
                                  borderRadius: 8,
                                  padding: '8px 10px',
                                  marginBottom: 10,
                                  fontSize: 10,
                                  color: '#065f46',
                                }}
                              >
                                🚚 Miễn phí ship cho đơn từ{' '}
                                <strong>{cartConfig.freeShippingNoticeThreshold.toLocaleString()}đ</strong>
                              </div>
                            )}

                            {/* Sample Cart Item */}
                            <div
                              style={{
                                background: '#ffffff',
                                borderRadius: 10,
                                border: '1px solid #f3f4f6',
                                padding: 10,
                                display: 'flex',
                                gap: 8,
                                marginBottom: 12,
                              }}
                            >
                              <div
                                style={{
                                  width: 44,
                                  height: 44,
                                  borderRadius: 6,
                                  background: '#fed7aa',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <CoffeeOutlined style={{ color: '#ea580c' }} />
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 11, fontWeight: 700 }}>Cold Brew Cam Sả</div>
                                <div style={{ fontSize: 9, color: '#6b7280' }}>Ít đá, 50% đường</div>
                                <div style={{ fontSize: 11, color: '#ea580c', fontWeight: 800, marginTop: 2 }}>
                                  55.000 ₫
                                </div>
                              </div>
                              <div style={{ fontSize: 10, fontWeight: 700, alignSelf: 'center' }}>x 1</div>
                            </div>

                            {/* Suggested Notes */}
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
                                📝 Gợi ý ghi chú cho quán
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                {(cartConfig?.suggestedNotes || []).map((note, i) => (
                                  <span
                                    key={i}
                                    style={{
                                      fontSize: 9,
                                      background: '#f3f4f6',
                                      border: '1px solid #e5e7eb',
                                      padding: '2px 6px',
                                      borderRadius: 4,
                                      color: '#374151',
                                    }}
                                  >
                                    + {note}
                                  </span>
                                ))}
                              </div>
                            </div>

                            {/* Checkout Button */}
                            <div
                              style={{
                                marginTop: 20,
                                background: '#ea580c',
                                color: '#ffffff',
                                borderRadius: 10,
                                padding: '8px 0',
                                textAlign: 'center',
                                fontSize: 11,
                                fontWeight: 700,
                              }}
                            >
                              Đặt hàng ngay (55.000đ)
                            </div>
                          </div>
                        )}

                        {/* PREVIEW TAB 5: CÁ NHÂN */}
                        {simulatorActiveTab === 'profile' && (
                          <div style={{ padding: '12px 10px' }}>
                            {/* Membership Card */}
                            {profileConfig?.showMembershipCard && (
                              <div
                                style={{
                                  background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
                                  borderRadius: 14,
                                  padding: 12,
                                  color: '#ffffff',
                                  marginBottom: 12,
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                }}
                              >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <span style={{ fontSize: 10, color: '#fbbf24', fontWeight: 800 }}>8AM MEMBER</span>
                                  <span style={{ fontSize: 9, background: '#374151', padding: '1px 6px', borderRadius: 4 }}>
                                    Hội viên VIP
                                  </span>
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 700, marginTop: 6 }}>Nguyễn Văn A</div>
                                <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
                                  Điểm tích lũy: <strong style={{ color: '#fbbf24' }}>150 điểm</strong>
                                </div>
                              </div>
                            )}

                            {/* Quick Menu */}
                            <div
                              style={{
                                background: '#ffffff',
                                borderRadius: 10,
                                border: '1px solid #f3f4f6',
                                overflow: 'hidden',
                                marginBottom: 12,
                              }}
                            >
                              {(profileConfig?.quickMenuItems || []).map((item) => (
                                <div
                                  key={item.id}
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '8px 10px',
                                    borderBottom: '1px solid #f9fafb',
                                    fontSize: 11,
                                    color: '#374151',
                                  }}
                                >
                                  <span>{item.title}</span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    {item.badge && (
                                      <span
                                        style={{
                                          fontSize: 8,
                                          background: '#fee2e2',
                                          color: '#dc2626',
                                          padding: '1px 4px',
                                          borderRadius: 3,
                                          fontWeight: 700,
                                        }}
                                      >
                                        {item.badge}
                                      </span>
                                    )}
                                    <span style={{ color: '#9ca3af' }}>&gt;</span>
                                  </div>
                                </div>
                              ))}
                            </div>

                            {/* Hotline info */}
                            <div
                              style={{
                                background: '#f8fafc',
                                borderRadius: 8,
                                padding: 8,
                                fontSize: 10,
                                color: '#6b7280',
                                textAlign: 'center',
                              }}
                            >
                              <div>📞 Hotline: <strong style={{ color: '#ea580c' }}>{profileConfig?.hotline}</strong></div>
                              <div style={{ fontSize: 9, marginTop: 2 }}>{profileConfig?.address}</div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Bottom Navigation (Bấm vào tab nào sẽ chuyển cả Admin và Phone Preview) */}
                      <div className="phone-bottom-nav">
                        <div
                          className={`phone-nav-item ${simulatorActiveTab === 'foryou' ? 'active' : ''}`}
                          onClick={() => handleTabChange('foryou')}
                        >
                          <HomeOutlined />
                          <span>Cho bạn</span>
                        </div>
                        <div
                          className={`phone-nav-item ${simulatorActiveTab === 'products' ? 'active' : ''}`}
                          onClick={() => handleTabChange('products')}
                        >
                          <CoffeeOutlined />
                          <span>Sản phẩm</span>
                        </div>
                        <div
                          className={`phone-nav-item ${simulatorActiveTab === 'search' ? 'active' : ''}`}
                          onClick={() => handleTabChange('search')}
                        >
                          <SearchOutlined />
                          <span>Tìm kiếm</span>
                        </div>
                        <div
                          className={`phone-nav-item ${simulatorActiveTab === 'cart' ? 'active' : ''}`}
                          onClick={() => handleTabChange('cart')}
                        >
                          <ShoppingCartOutlined />
                          <span>Giỏ hàng</span>
                        </div>
                        <div
                          className={`phone-nav-item ${simulatorActiveTab === 'profile' ? 'active' : ''}`}
                          onClick={() => handleTabChange('profile')}
                        >
                          <UserOutlined />
                          <span>Cá nhân</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* MODAL: CHỈNH SỬA CHI TIẾT DANH MỤC */}
          <Modal
            title={
              <Space>
                <EditOutlined style={{ color: '#059669' }} />
                <span>Chỉnh sửa danh mục: {editingCategory?.name}</span>
              </Space>
            }
            open={editCategoryModalVisible}
            onOk={handleSaveCategoryModal}
            onCancel={() => setEditCategoryModalVisible(false)}
            width={720}
            okText="Lưu thay đổi"
            cancelText="Hủy"
          >
            {/* Box xem trước trực quan tức thì */}
            <div
              style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 12,
                padding: '12px 16px',
                marginTop: 10,
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <div style={{ fontSize: 10, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  👁️ Xem trước hiển thị trên Zalo Mini App
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                  <div
                    style={{
                      width: 50,
                      height: 50,
                      borderRadius: 12,
                      background: editFormGradient || '#FFF8E7',
                      border: `1.5px solid ${editFormColor || '#8B4513'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                    }}
                  >
                    {editFormImageUrl ? (
                      <img src={editFormImageUrl} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      renderCategoryIcon(editFormIconName, { color: editFormColor || '#8B4513', fontSize: 22 })
                    )}
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontWeight: 800, fontSize: 14, color: '#1e293b' }}>
                        {editFormName || 'Tên danh mục'}
                      </span>
                      {editFormBadge && (
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 800,
                            padding: '1px 6px',
                            borderRadius: 4,
                            background: '#fee2e2',
                            color: '#dc2626',
                          }}
                        >
                          {editFormBadge}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                      {editFormSubtitle || 'Mô tả phụ hiển thị bên dưới'}
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <Tag color={editFormImageUrl ? 'green' : 'blue'}>
                  {editFormImageUrl ? '📷 Đang dùng ảnh đại diện' : '✨ Đang dùng Icon hệ thống'}
                </Tag>
              </div>
            </div>

            <Form form={categoryForm} layout="vertical">
              <Row gutter={16}>
                <Col span={14}>
                  <Form.Item
                    name="name"
                    label="Tên danh mục hiển thị"
                    rules={[{ required: true, message: 'Vui lòng nhập tên danh mục' }]}
                  >
                    <Input placeholder="Ví dụ: Specialty, Cold Brew, Arabica Base..." />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item name="badge" label="Huy hiệu (Badge)">
                    <Input placeholder="Ví dụ: HOT, NEW, ĐẶC BIỆT..." />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="subtitle" label="Mô tả phụ / Slogan ngắn">
                <Input placeholder="Ví dụ: Cà phê đặc sản chọn lọc rang mộc..." />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="sourceType" label="Loại nguồn dữ liệu">
                    <Select>
                      <Select.Option value="cukcuk">☕ Nhóm món CUKCUK</Select.Option>
                      <Select.Option value="coffee">🏪 Hạt cà phê</Select.Option>
                      <Select.Option value="bottledDrink">🛍️ Đồ uống đóng chai</Select.Option>
                      <Select.Option value="equipment">🛠️ Dụng cụ cà phê</Select.Option>
                      <Select.Option value="news">📰 Tin tức & Ưu đãi</Select.Option>
                      <Select.Option value="custom">✨ Tùy chỉnh</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="cukcukGroupName" label="Nhóm CUKCUK liên kết">
                    <Select placeholder="Chọn nhóm CUKCUK (nếu là CUKCUK)" allowClear>
                      {cukcukGroups.map((g) => (
                        <Select.Option key={g.groupCode} value={g.groupName}>
                          {g.groupName} ({g.dishCount} món)
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item name="displayLayout" label="Kiểu hiển thị trên trang">
                    <Select>
                      <Select.Option value="horizontal_scroll">Cuộn ngang (Carousel)</Select.Option>
                      <Select.Option value="grid_2_cols">Lưới 2 cột (Grid 2)</Select.Option>
                      <Select.Option value="grid_3_cols">Lưới 3 cột (Grid 3)</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {/* PHẦN ẢNH ĐẠI DIỆN */}
              <Divider orientation="left" style={{ margin: '14px 0 10px 0', fontSize: 13 }}>
                <Space>
                  <PictureOutlined style={{ color: '#0284c7' }} />
                  <span>Ảnh đại diện danh mục</span>
                </Space>
              </Divider>

              <div
                style={{
                  background: '#f8fafc',
                  border: '1px dashed #cbd5e1',
                  borderRadius: 10,
                  padding: 12,
                  marginBottom: 16,
                }}
              >
                <Row gutter={16} align="middle">
                  <Col xs={24} sm={10}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 8,
                          background: '#e2e8f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                        }}
                      >
                        {editFormImageUrl ? (
                          <img src={editFormImageUrl} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <PictureOutlined style={{ fontSize: 24, color: '#94a3b8' }} />
                        )}
                      </div>
                      <div>
                        <Upload
                          beforeUpload={(file) => handleUploadCategoryPhoto(file, categoryForm)}
                          showUploadList={false}
                          accept="image/*"
                        >
                          <Button icon={<UploadOutlined />} loading={uploadingImage} size="small" type="primary" ghost>
                            Tải ảnh từ máy tính
                          </Button>
                        </Upload>
                        {editFormImageUrl && (
                          <Button
                            danger
                            type="text"
                            size="small"
                            icon={<DeleteOutlined />}
                            style={{ display: 'block', marginTop: 4, padding: 0 }}
                            onClick={() => categoryForm.setFieldsValue({ imageUrl: '' })}
                          >
                            Xóa ảnh (quay về icon)
                          </Button>
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} sm={14}>
                    <Form.Item name="imageUrl" label="Hoặc nhập đường dẫn ảnh (URL)" style={{ marginBottom: 0 }}>
                      <Input placeholder="https://..." allowClear />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* PHẦN THƯ VIỆN ICON ĐA DẠNG */}
              <Divider orientation="left" style={{ margin: '14px 0 10px 0', fontSize: 13 }}>
                <Space>
                  <CoffeeOutlined style={{ color: '#ea580c' }} />
                  <span>Thư viện Icon (Tự động hiển thị khi không dùng ảnh)</span>
                </Space>
              </Divider>

              <Form.Item name="iconName" label="Chọn icon phù hợp cho danh mục:" style={{ marginBottom: 12 }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                    gap: 8,
                    maxHeight: 180,
                    overflowY: 'auto',
                    padding: 8,
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 10,
                  }}
                >
                  {AVAILABLE_ICONS.map((item) => {
                    const isSelected = (editFormIconName || 'CoffeeOutlined') === item.key;
                    const IconCmp = item.component;
                    return (
                      <div
                        key={item.key}
                        onClick={() => categoryForm.setFieldsValue({ iconName: item.key })}
                        style={{
                          cursor: 'pointer',
                          padding: '6px 10px',
                          borderRadius: 8,
                          border: isSelected ? '2px solid #ea580c' : '1px solid #e2e8f0',
                          background: isSelected ? '#fff7ed' : '#ffffff',
                          color: isSelected ? '#ea580c' : '#334155',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          transition: 'all 0.15s ease',
                          boxShadow: isSelected ? '0 2px 6px rgba(234, 88, 12, 0.15)' : 'none',
                        }}
                      >
                        <IconCmp style={{ fontSize: 18, color: isSelected ? '#ea580c' : '#64748b' }} />
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: isSelected ? 700 : 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {item.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </Form.Item>

              {/* PHẦN MÀU SẮC & GRADIENT */}
              <Divider orientation="left" style={{ margin: '14px 0 10px 0', fontSize: 13 }}>
                Màu sắc & Gradient đại diện
              </Divider>

              <div style={{ marginBottom: 12 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Chọn preset màu & icon nhanh:
                </Text>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                  {PRESET_THEMES.map((theme, i) => (
                    <Tag
                      key={i}
                      style={{
                        cursor: 'pointer',
                        padding: '4px 8px',
                        borderRadius: 6,
                        border: `1px solid ${theme.color}`,
                        background: theme.gradient,
                        color: theme.color,
                        fontWeight: 600,
                      }}
                      onClick={() => {
                        categoryForm.setFieldsValue({
                          colorTheme: theme.color,
                          gradient: theme.gradient,
                          iconName: theme.iconName || 'CoffeeOutlined',
                        });
                      }}
                    >
                      {theme.name}
                    </Tag>
                  ))}
                </div>
              </div>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="colorTheme" label="Màu chủ đạo (Hex code)">
                    <Input placeholder="#C67C4E" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="gradient" label="CSS Gradient nền">
                    <Input placeholder="linear-gradient(...)" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="showInMainCategories" valuePropName="checked">
                    <Switch /> Hiện ở thanh danh mục trên cùng
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="showAsFeaturedSection" valuePropName="checked">
                    <Switch /> Hiện thành section món cuộn bên dưới
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Modal>

          {/* MODAL: THÊM DANH MỤC MỚI */}
          <Modal
            title={
              <Space>
                <PlusOutlined style={{ color: '#0284c7' }} />
                <span>Thêm danh mục mới vào Mini App</span>
              </Space>
            }
            open={addCategoryModalVisible}
            onOk={handleAddNewCategory}
            onCancel={() => setAddCategoryModalVisible(false)}
            width={720}
            okText="Thêm ngay"
            cancelText="Hủy"
          >
            {/* Box xem trước trực quan tức thì */}
            <div
              style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 12,
                padding: '12px 16px',
                marginTop: 10,
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <div style={{ fontSize: 10, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  👁️ Xem trước hiển thị trên Zalo Mini App
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
                  <div
                    style={{
                      width: 50,
                      height: 50,
                      borderRadius: 12,
                      background: addFormGradient || '#FFF3E0',
                      border: `1.5px solid ${addFormColor || '#C67C4E'}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                    }}
                  >
                    {addFormImageUrl ? (
                      <img src={addFormImageUrl} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      renderCategoryIcon(addFormIconName, { color: addFormColor || '#C67C4E', fontSize: 22 })
                    )}
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontWeight: 800, fontSize: 14, color: '#1e293b' }}>
                        {addFormName || 'Tên danh mục'}
                      </span>
                      {addFormBadge && (
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 800,
                            padding: '1px 6px',
                            borderRadius: 4,
                            background: '#fee2e2',
                            color: '#dc2626',
                          }}
                        >
                          {addFormBadge}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                      {addFormSubtitle || 'Mô tả phụ hiển thị bên dưới'}
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <Tag color={addFormImageUrl ? 'green' : 'blue'}>
                  {addFormImageUrl ? '📷 Đang dùng ảnh đại diện' : '✨ Đang dùng Icon hệ thống'}
                </Tag>
              </div>
            </div>

            <Form form={addCategoryForm} layout="vertical">
              {unusedCukcukGroups.length > 0 && (
                <div style={{ background: '#f0f9ff', padding: 12, borderRadius: 8, marginBottom: 16 }}>
                  <Text style={{ fontWeight: 600, color: '#0369a1' }}>💡 Nhóm CUKCUK có sẵn chưa thêm:</Text>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                    {unusedCukcukGroups.map((g) => (
                      <Tag
                        key={g.groupCode}
                        color="blue"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          addCategoryForm.setFieldsValue({
                            name: g.groupName,
                            code: g.groupCode,
                            cukcukGroupName: g.groupName,
                            subtitle: `Thực đơn ${g.groupName}`,
                            imageUrl: g.sampleImageUrl,
                          });
                        }}
                      >
                        + {g.groupName} ({g.dishCount} món)
                      </Tag>
                    ))}
                  </div>
                </div>
              )}

              <Row gutter={16}>
                <Col span={14}>
                  <Form.Item
                    name="name"
                    label="Tên danh mục"
                    rules={[{ required: true, message: 'Vui lòng nhập tên danh mục' }]}
                  >
                    <Input placeholder="Ví dụ: Specialty, Cold Brew, Trà sữa..." />
                  </Form.Item>
                </Col>
                <Col span={10}>
                  <Form.Item
                    name="code"
                    label="Mã định danh (Slug)"
                    rules={[{ required: true, message: 'Vui lòng nhập mã slug' }]}
                  >
                    <Input placeholder="specialty, cold-brew..." />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="subtitle" label="Mô tả phụ">
                <Input placeholder="Mô tả ngắn hiển thị bên dưới tên danh mục" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="sourceType" label="Loại nguồn dữ liệu" initialValue="cukcuk">
                    <Select>
                      <Select.Option value="cukcuk">☕ Nhóm CUKCUK</Select.Option>
                      <Select.Option value="coffee">🏪 Hạt cà phê</Select.Option>
                      <Select.Option value="bottledDrink">🛍️ Đóng chai</Select.Option>
                      <Select.Option value="equipment">🛠️ Dụng cụ cà phê</Select.Option>
                      <Select.Option value="news">📰 Tin tức & Ưu đãi</Select.Option>
                      <Select.Option value="custom">✨ Tùy chỉnh</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="cukcukGroupName" label="Nhóm CUKCUK (Nếu là CUKCUK)">
                    <Select placeholder="Chọn nhóm CUKCUK" allowClear>
                      {cukcukGroups.map((g) => (
                        <Select.Option key={g.groupCode} value={g.groupName}>
                          {g.groupName} ({g.dishCount} món)
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="badge" label="Huy hiệu (Badge)">
                    <Input placeholder="HOT, MỚI, SALE..." />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="displayLayout" label="Kiểu hiển thị" initialValue="horizontal_scroll">
                    <Select>
                      <Select.Option value="horizontal_scroll">Cuộn ngang (Carousel)</Select.Option>
                      <Select.Option value="grid_2_cols">Lưới 2 cột (Grid 2)</Select.Option>
                      <Select.Option value="grid_3_cols">Lưới 3 cột (Grid 3)</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {/* PHẦN ẢNH ĐẠI DIỆN */}
              <Divider orientation="left" style={{ margin: '14px 0 10px 0', fontSize: 13 }}>
                <Space>
                  <PictureOutlined style={{ color: '#0284c7' }} />
                  <span>Ảnh đại diện danh mục</span>
                </Space>
              </Divider>

              <div
                style={{
                  background: '#f8fafc',
                  border: '1px dashed #cbd5e1',
                  borderRadius: 10,
                  padding: 12,
                  marginBottom: 16,
                }}
              >
                <Row gutter={16} align="middle">
                  <Col xs={24} sm={10}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 8,
                          background: '#e2e8f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                        }}
                      >
                        {addFormImageUrl ? (
                          <img src={addFormImageUrl} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <PictureOutlined style={{ fontSize: 24, color: '#94a3b8' }} />
                        )}
                      </div>
                      <div>
                        <Upload
                          beforeUpload={(file) => handleUploadCategoryPhoto(file, addCategoryForm)}
                          showUploadList={false}
                          accept="image/*"
                        >
                          <Button icon={<UploadOutlined />} loading={uploadingImage} size="small" type="primary" ghost>
                            Tải ảnh từ máy tính
                          </Button>
                        </Upload>
                        {addFormImageUrl && (
                          <Button
                            danger
                            type="text"
                            size="small"
                            icon={<DeleteOutlined />}
                            style={{ display: 'block', marginTop: 4, padding: 0 }}
                            onClick={() => addCategoryForm.setFieldsValue({ imageUrl: '' })}
                          >
                            Xóa ảnh (quay về icon)
                          </Button>
                        )}
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} sm={14}>
                    <Form.Item name="imageUrl" label="Hoặc nhập đường dẫn ảnh (URL)" style={{ marginBottom: 0 }}>
                      <Input placeholder="https://..." allowClear />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* PHẦN THƯ VIỆN ICON ĐA DẠNG */}
              <Divider orientation="left" style={{ margin: '14px 0 10px 0', fontSize: 13 }}>
                <Space>
                  <CoffeeOutlined style={{ color: '#ea580c' }} />
                  <span>Thư viện Icon (Tự động hiển thị khi không dùng ảnh)</span>
                </Space>
              </Divider>

              <Form.Item name="iconName" label="Chọn icon phù hợp cho danh mục:" style={{ marginBottom: 12 }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                    gap: 8,
                    maxHeight: 180,
                    overflowY: 'auto',
                    padding: 8,
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 10,
                  }}
                >
                  {AVAILABLE_ICONS.map((item) => {
                    const isSelected = (addFormIconName || 'CoffeeOutlined') === item.key;
                    const IconCmp = item.component;
                    return (
                      <div
                        key={item.key}
                        onClick={() => addCategoryForm.setFieldsValue({ iconName: item.key })}
                        style={{
                          cursor: 'pointer',
                          padding: '6px 10px',
                          borderRadius: 8,
                          border: isSelected ? '2px solid #ea580c' : '1px solid #e2e8f0',
                          background: isSelected ? '#fff7ed' : '#ffffff',
                          color: isSelected ? '#ea580c' : '#334155',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          transition: 'all 0.15s ease',
                          boxShadow: isSelected ? '0 2px 6px rgba(234, 88, 12, 0.15)' : 'none',
                        }}
                      >
                        <IconCmp style={{ fontSize: 18, color: isSelected ? '#ea580c' : '#64748b' }} />
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: isSelected ? 700 : 500,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {item.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </Form.Item>

              {/* PHẦN MÀU SẮC & GRADIENT */}
              <Divider orientation="left" style={{ margin: '14px 0 10px 0', fontSize: 13 }}>
                Màu sắc & Gradient đại diện
              </Divider>

              <div style={{ marginBottom: 12 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Chọn preset màu & icon nhanh:
                </Text>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                  {PRESET_THEMES.map((theme, i) => (
                    <Tag
                      key={i}
                      style={{
                        cursor: 'pointer',
                        padding: '4px 8px',
                        borderRadius: 6,
                        border: `1px solid ${theme.color}`,
                        background: theme.gradient,
                        color: theme.color,
                        fontWeight: 600,
                      }}
                      onClick={() => {
                        addCategoryForm.setFieldsValue({
                          colorTheme: theme.color,
                          gradient: theme.gradient,
                          iconName: theme.iconName || 'CoffeeOutlined',
                        });
                      }}
                    >
                      {theme.name}
                    </Tag>
                  ))}
                </div>
              </div>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="colorTheme" label="Màu chủ đạo (Hex code)">
                    <Input placeholder="#C67C4E" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="gradient" label="CSS Gradient nền">
                    <Input placeholder="linear-gradient(...)" />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="showInMainCategories" valuePropName="checked" initialValue={true}>
                    <Switch defaultChecked /> Hiện ở thanh danh mục trên cùng
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="showAsFeaturedSection" valuePropName="checked" initialValue={true}>
                    <Switch defaultChecked /> Hiện thành khối section cuộn bên dưới
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Modal>

          {/* DRAWER: QUẢN LÝ MÓN ĂN & SẢN PHẨM TRONG DANH MỤC */}
          <Drawer
            title={
              <Space>
                <AppstoreOutlined style={{ color: '#2563eb' }} />
                <span>
                  Danh sách sản phẩm: <strong>{selectedCategoryForDishes?.name}</strong>
                </span>
                <Tag color="blue">
                  {currentCategoryDishes.length} {selectedCategoryForDishes?.sourceType === 'news' ? 'bài' : 'sản phẩm'}
                </Tag>
              </Space>
            }
            placement="right"
            width={600}
            open={manageDishesDrawerVisible}
            onClose={() => setManageDishesDrawerVisible(false)}
          >
            <div style={{ marginBottom: 16 }}>
              <Input
                prefix={<SearchOutlined />}
                placeholder="Tìm kiếm theo tên hoặc mã sản phẩm..."
                value={dishSearchTerm}
                onChange={(e) => setDishSearchTerm(e.target.value)}
                allowClear
              />
            </div>

            {currentCategoryDishes.length === 0 ? (
              <Empty description="Không tìm thấy món hoặc sản phẩm nào trong nhóm này" />
            ) : (
              <Table
                dataSource={currentCategoryDishes}
                rowKey="id"
                pagination={{ pageSize: 8 }}
                size="middle"
                columns={[
                  {
                    title: 'Ảnh',
                    key: 'image',
                    width: 60,
                    render: (_, record) => (
                      <div
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 8,
                          background: '#f3f4f6',
                          overflow: 'hidden',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {record.imageUrl ? (
                          <img
                            src={record.imageUrl}
                            alt={record.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <CoffeeOutlined style={{ color: '#9ca3af', fontSize: 18 }} />
                        )}
                      </div>
                    ),
                  },
                  {
                    title: 'Tên món & Giá',
                    key: 'info',
                    render: (_, record) => (
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#111827' }}>{record.name}</div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>Mã: {record.code || '—'}</div>
                        <div style={{ fontSize: 12, color: '#ea580c', fontWeight: 700, marginTop: 2 }}>
                          {(record.price || 0).toLocaleString()} ₫
                        </div>
                      </div>
                    ),
                  },
                  {
                    title: 'Nổi bật',
                    key: 'featured',
                    width: 70,
                    align: 'center',
                    render: (_, record) => {
                      const isFeatured = (selectedCategoryForDishes?.featuredProductIds || []).includes(record.id);
                      return (
                        <Tooltip title={isFeatured ? 'Món nổi bật (Đang bật)' : 'Đánh dấu món nổi bật'}>
                          <Button
                            type="text"
                            size="small"
                            icon={
                              isFeatured ? (
                                <StarFilled style={{ color: '#f59e0b', fontSize: 18 }} />
                              ) : (
                                <StarOutlined style={{ color: '#d1d5db', fontSize: 18 }} />
                              )
                            }
                            onClick={() => handleToggleDishFeatured(record.id)}
                          />
                        </Tooltip>
                      );
                    },
                  },
                  {
                    title: 'Hiển thị',
                    key: 'visibility',
                    width: 80,
                    align: 'center',
                    render: (_, record) => {
                      const isExcluded = (selectedCategoryForDishes?.excludedProductIds || []).includes(record.id);
                      return (
                        <Switch
                          checked={!isExcluded}
                          onChange={() => handleToggleDishExclusion(record.id)}
                          checkedChildren={<CheckOutlined />}
                        />
                      );
                    },
                  },
                ]}
              />
            )}
          </Drawer>
        </div>
      </ConfigProvider>
    </AdminLayout>
  );
};

export default MiniAppManagement;
